import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarDays, Clock, Mic2, X } from 'lucide-react';
import { useTodaysRoom } from '../hooks/useTodaysRoom';

const TodaysRoomSection = () => {
  const room = useTodaysRoom();
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const [isMuted, setIsMuted] = useState(true);
  const vidRef = useRef(null);

  const videoRef = useCallback((node) => {
    if (!node) return;
    vidRef.current = node;
    node.loop = true;
    node.muted = true;
    node.playsInline = true;

    const tryPlay = () => {
      if (node.paused) node.play().catch(() => {});
    };

    // Fire play on every load milestone the browser emits
    node.addEventListener('loadedmetadata', tryPlay);
    node.addEventListener('loadeddata', tryPlay);
    node.addEventListener('canplay', tryPlay);
    tryPlay();
  }, []);

  // Polling fallback: if the ref play didn't stick, retry until video is rolling
  React.useEffect(() => {
    const vid = vidRef.current;
    if (!vid) return;
    const id = setInterval(() => {
      if (vid.paused && vid.readyState >= 2) {
        vid.play().catch(() => {});
      }
      if (!vid.paused) clearInterval(id);
    }, 400);
    return () => clearInterval(id);
  }, [room]);

  const handleVideoTap = () => {
    const vid = vidRef.current;
    if (!vid) return;
    vid.muted = !vid.muted;
    setIsMuted(vid.muted);
    vid.play().catch(() => {});
  };

  if (!room) {
    return (
      <section className="px-4 pb-16" data-testid="todays-room-section">
        <div className="max-w-3xl mx-auto text-center py-10">
          <p className="text-white/15 text-sm">Today's room will be announced soon.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="px-4 pb-16" data-testid="todays-room-section">
      <div className="max-w-3xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F97316]/10 border border-[#F97316]/20 mb-3">
              <CalendarDays size={12} className="text-[#F97316]" />
              <span className="text-[#F97316] text-[10px] font-bold tracking-widest uppercase">Today's Room</span>
            </div>
            {room.title && (
              <h2 className="text-white text-xl sm:text-2xl font-bold" data-testid="todays-room-title">{room.title}</h2>
            )}
          </div>

          {/* Media */}
          <div className="glass-card overflow-hidden mb-4 relative" data-testid="todays-room-media">
            {room.media_type === 'video' && room.media_url ? (
              <div className="relative">
                <video
                  ref={videoRef}
                  src={room.media_url}
                  poster={room.poster_url || undefined}
                  preload="auto"
                  autoPlay
                  loop
                  muted
                  playsInline
                  onClick={handleVideoTap}
                  onLoadedData={(e) => { if (e.target.paused) e.target.play().catch(() => {}); }}
                  onCanPlay={(e) => { if (e.target.paused) e.target.play().catch(() => {}); }}
                  onEnded={(e) => { e.target.currentTime = 0; e.target.play(); }}
                  className="w-full cursor-pointer"
                  style={{ objectFit: 'contain', maxHeight: '80vh' }}
                  data-testid="todays-room-video"
                />
                <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm text-white/70 text-[10px] px-2.5 py-1 rounded-full pointer-events-none" data-testid="mute-indicator">
                  {isMuted ? 'Tap for sound' : 'Sound on'}
                </div>
              </div>
            ) : room.media_url ? (
              <div
                className="cursor-pointer"
                onClick={() => setLightboxOpen(true)}
                data-testid="todays-room-flyer-trigger"
              >
                <img
                  src={room.media_url}
                  alt={room.title || "Today's Room Flyer"}
                  className="w-full h-auto"
                  style={{ objectFit: 'contain' }}
                  data-testid="todays-room-flyer"
                />
                <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
                  <span className="bg-black/60 text-white/70 text-xs px-3 py-1.5 rounded-full backdrop-blur-sm">Tap to enlarge</span>
                </div>
              </div>
            ) : null}
          </div>

          {/* Info */}
          <div className="glass-card p-5 sm:p-6" data-testid="todays-room-info">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              {room.date && (
                <span className="flex items-center gap-1.5 text-[#F97316] text-xs">
                  <CalendarDays size={12} /> {room.date}
                </span>
              )}
              {room.time && (
                <span className="flex items-center gap-1.5 text-white/50 text-xs">
                  <Clock size={12} /> {room.time}
                </span>
              )}
              {room.host && (
                <span className="flex items-center gap-1.5 text-white/50 text-xs">
                  <Mic2 size={12} /> {room.host}
                </span>
              )}
            </div>
            {room.description && (
              <p className="text-white/60 text-sm leading-relaxed" data-testid="todays-room-description">{room.description}</p>
            )}
            {room.additional_details && (
              <p className="text-white/30 text-xs mt-3">{room.additional_details}</p>
            )}
          </div>
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && room.media_type === 'image' && room.media_url && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setLightboxOpen(false)}
            data-testid="todays-room-lightbox"
          >
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              data-testid="lightbox-close"
            >
              <X size={20} className="text-white" />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              src={room.media_url}
              alt={room.title || "Today's Room Flyer"}
              className="max-w-full max-h-[90vh]"
              style={{ objectFit: 'contain' }}
              onClick={e => e.stopPropagation()}
              data-testid="lightbox-image"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default TodaysRoomSection;
